import { useCallback, useEffect, useRef, useState } from 'react'

import { MAX_BATCH_IMAGES, getImageMaxBytes } from '../constants/channelLimits'
import { compressImageForUpload } from '../lib/compressImage'

// Batch image send, split into the two phases the staff-facing flow needs.
//
// Phase A (add/retryUpload/remove) prepares images in a local preview tray:
// validate type, compress to the channel's ceiling, upload to storage. Nothing
// reaches the customer here, so a failure is fully recoverable and shows up as
// a bad card in the tray with its own retry. Items are processed concurrently
// and independently, since ordering carries no meaning until send.
//
// Phase B (send) commits the tray: one optimistic append for the whole batch,
// one API call, then per-item patches from the response.
//
// Tray state is deliberately component-local rather than in the TanStack Query
// cache: it is the scratch state of one unsent composition, not server data,
// and it holds File objects and blob URLs that must never be cached or shared
// between threads.
export function useImageBatchSend({
  tenantId,
  contactId,
  channel,
  userId,
  appendDetailMessages,
  patchDetailMessage,
  refreshThreadAndList,
  sendImages,
  uploadImage,
  onError,
}) {
  const [items, setItems] = useState([])
  const [isSending, setIsSending] = useState(false)

  // The callbacks below need the current item list without being rebuilt every
  // time it changes: `send` and `add` are passed to JSX handlers, and a new
  // identity on each keystroke-driven render would churn them needlessly. The
  // ref is written in an effect rather than during render, since a render that
  // React discards must not be able to publish state through it.
  const itemsRef = useRef(items)
  useEffect(() => {
    itemsRef.current = items
  }, [items])

  // How many items the tray holds, counted as `add` admits them instead of
  // after a render, so the batch cap is enforced against calls that have not
  // been rendered yet. Every path that drops an item decrements it, otherwise
  // the usable cap would ratchet down over a session.
  const pendingCountRef = useRef(0)

  // Unmount cleanup reads the ref once, so it does not re-run per list change
  // and cannot revoke a URL the tray is still rendering.
  useEffect(() => () => {
    for (const item of itemsRef.current) {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
    }
  }, [])

  const patchItem = useCallback((localId, patch) => {
    setItems(current => current.map(item => (item.localId === localId ? { ...item, ...patch } : item)))
  }, [])

  // Compress then upload one item, moving it through its own states. Never
  // throws: every failure lands on the item as an errorKind the tray renders,
  // because one bad image must not abort its siblings.
  const prepareItem = useCallback(async (localId, file) => {
    const maxBytes = getImageMaxBytes(channel)
    let prepared = file
    patchItem(localId, { status: 'compressing', errorKind: undefined, errorDetail: undefined })
    try {
      prepared = await compressImageForUpload(file, maxBytes)
    } catch (err) {
      patchItem(localId, {
        status: 'error',
        errorKind: err?.message === 'image_still_too_large' ? 'too_large' : 'upload_failed',
        errorDetail: err?.message,
      })
      return
    }

    if (prepared.size > maxBytes) {
      patchItem(localId, { status: 'error', errorKind: 'too_large', errorDetail: `${prepared.size} > ${maxBytes}` })
      return
    }

    patchItem(localId, { status: 'uploading' })
    try {
      const res = await uploadImage(tenantId, prepared)
      const url = res?.url
      if (!url) throw new Error('missing image url')
      patchItem(localId, {
        status: 'uploaded',
        uploadedUrl: url,
        size: res?.size ?? prepared.size,
        errorKind: undefined,
        errorDetail: undefined,
      })
    } catch (err) {
      patchItem(localId, { status: 'error', errorKind: 'upload_failed', errorDetail: err?.message })
    }
  }, [channel, patchItem, tenantId, uploadImage])

  // Accepts files up to the batch cap, counting what is already in the tray.
  // Returns the number of files turned away so the caller can tell the user
  // rather than dropping them silently.
  const add = useCallback((fileList) => {
    const incoming = Array.from(fileList || [])
    if (!incoming.length) return { accepted: 0, rejectedForCap: 0 }

    const candidates = incoming.map(file => {
      const isImage = Boolean(file.type?.startsWith('image/'))
      return {
        localId: crypto.randomUUID(),
        file,
        // An invalid type gets no object URL at all: there is nothing to
        // preview, and allocating one would leak since it is never rendered
        // or revoked.
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        status: isImage ? 'compressing' : 'error',
        errorKind: isImage ? undefined : 'invalid_type',
      }
    })

    // `pending` is the authoritative count of what this hook has admitted,
    // bumped synchronously here rather than read back from state. Two adds in
    // one tick must not both measure the same not-yet-rendered length and
    // together overshoot the batch limit, and a setState updater is not a
    // place to learn the answer from: React may run it later, or twice.
    const room = Math.max(0, MAX_BATCH_IMAGES - pendingCountRef.current)
    const admitted = candidates.slice(0, room)
    for (const over of candidates.slice(room)) {
      // Trimmed candidates never reach the tray, so nothing would ever revoke
      // their preview URL; do it here or leak it.
      if (over.previewUrl) URL.revokeObjectURL(over.previewUrl)
    }
    pendingCountRef.current += admitted.length

    if (admitted.length) setItems(current => [...current, ...admitted])

    for (const item of admitted) {
      if (item.status !== 'error') prepareItem(item.localId, item.file)
    }

    return { accepted: admitted.length, rejectedForCap: incoming.length - admitted.length }
  }, [prepareItem])

  const retryUpload = useCallback((localId) => {
    const item = itemsRef.current.find(i => i.localId === localId)
    if (!item || item.status !== 'error') return
    // An invalid type cannot be retried into validity; it must be removed.
    if (item.errorKind === 'invalid_type') return
    prepareItem(localId, item.file)
  }, [prepareItem])

  const remove = useCallback((localId) => {
    const item = itemsRef.current.find(i => i.localId === localId)
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
    setItems(current => {
      const next = current.filter(i => i.localId !== localId)
      pendingCountRef.current = next.length
      return next
    })
  }, [])

  const clear = useCallback(() => {
    for (const item of itemsRef.current) {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
    }
    pendingCountRef.current = 0
    setItems([])
  }, [])

  // Phase B. Resolves to a result object rather than throwing, so the caller
  // decides how to surface a total failure.
  const send = useCallback(async (captionText = '') => {
    const ready = itemsRef.current.filter(i => i.status === 'uploaded')
    if (!ready.length || !tenantId || !userId) return { sent: false, reason: 'nothing_to_send' }

    const caption = (captionText || '').trim()
    const captionLocalId = caption ? crypto.randomUUID() : null
    const timestamp = new Date().toISOString()

    const optimisticMessages = []
    if (captionLocalId) {
      optimisticMessages.push({
        _localId: captionLocalId,
        role: 'staff',
        content: caption,
        timestamp,
        _status: 'sending',
      })
    }
    for (const item of ready) {
      optimisticMessages.push({
        _localId: item.localId,
        role: 'staff',
        content: '',
        image_url: item.uploadedUrl,
        timestamp,
        _status: 'sending',
        // Carries everything a later retry of this one image needs, so the
        // retry does not have to reconstruct it from the tray, which is
        // already cleared by then.
        _retry: { kind: 'image', url: item.uploadedUrl, size: item.size, clientRef: item.localId },
      })
    }

    // One append for the batch. Calling the single-message helper N times would
    // stack N rollback closures, each snapshotting a different cache state.
    const rollback = appendDetailMessages(contactId, channel, optimisticMessages)
    const payload = ready.map(i => ({ url: i.uploadedUrl, size: i.size, client_ref: i.localId }))

    clear()
    setIsSending(true)
    try {
      const res = await sendImages(tenantId, userId, { images: payload, message: caption })
      const known = new Set(optimisticMessages.map(m => m._localId))

      for (const item of Array.isArray(res?.items) ? res.items : []) {
        const localId = item?.client_ref
        if (!localId || !known.has(localId)) {
          // A result we cannot place is a contract mismatch worth noticing, but
          // it must not throw: the items we can place still need their patches.
          console.warn('[useImageBatchSend] response item with unknown client_ref', item?.client_ref)
          continue
        }
        patchDetailMessage(contactId, channel, localId, item.status === 'sent'
          ? { _status: 'sent' }
          : {
            _status: 'failed',
            _errorCode: item.error_reason || null,
            _errorDetail: item.error_detail || null,
            _retriable: item.retriable !== false,
          })
      }

      if (captionLocalId) {
        patchDetailMessage(contactId, channel, captionLocalId, res?.caption_sent === false
          ? { _status: 'failed', _errorDetail: res?.caption_error || null, _retriable: false }
          : { _status: 'sent' })
      }

      await refreshThreadAndList(contactId, channel)
      return { sent: true, result: res }
    } catch (err) {
      // No response at all means the batch never ran, so nothing reached the
      // customer and removing the whole optimistic append is correct. This is
      // the only path that rolls back: a partial failure inside a 200 must
      // leave the delivered images in place.
      rollback()
      onError?.(err)
      return { sent: false, reason: 'request_failed', error: err }
    } finally {
      setIsSending(false)
    }
  }, [appendDetailMessages, channel, clear, contactId, onError, patchDetailMessage, refreshThreadAndList, sendImages, tenantId, userId])

  // Re-sends one already-failed image from the thread. Deliberately the same
  // endpoint as a full batch, with a single-item list and no caption: a retry
  // is a batch of one, not a separate code path.
  const retrySend = useCallback(async (localId, retryInfo) => {
    if (!retryInfo?.url || !tenantId || !userId) return { sent: false, reason: 'missing_retry_info' }

    patchDetailMessage(contactId, channel, localId, {
      _status: 'sending',
      _errorCode: null,
      _errorDetail: null,
    })
    try {
      const res = await sendImages(tenantId, userId, {
        images: [{ url: retryInfo.url, size: retryInfo.size, client_ref: localId }],
        message: '',
      })
      const item = (Array.isArray(res?.items) ? res.items : []).find(i => i?.client_ref === localId)
      if (!item) {
        patchDetailMessage(contactId, channel, localId, {
          _status: 'failed',
          _errorDetail: res?.fatal_error || 'no result for this image',
          _retriable: true,
        })
        return { sent: false, reason: 'no_item_in_response' }
      }
      patchDetailMessage(contactId, channel, localId, item.status === 'sent'
        ? { _status: 'sent' }
        : {
          _status: 'failed',
          _errorCode: item.error_reason || null,
          _errorDetail: item.error_detail || null,
          _retriable: item.retriable !== false,
        })
      if (item.status === 'sent') await refreshThreadAndList(contactId, channel)
      return { sent: item.status === 'sent', result: res }
    } catch (err) {
      // Leave it failed and retriable: the image is still in the thread and the
      // staff member can try again.
      patchDetailMessage(contactId, channel, localId, {
        _status: 'failed',
        _errorDetail: err?.message || null,
        _retriable: true,
      })
      onError?.(err)
      return { sent: false, reason: 'request_failed', error: err }
    }
  }, [channel, contactId, onError, patchDetailMessage, refreshThreadAndList, sendImages, tenantId, userId])

  const readyCount = items.filter(i => i.status === 'uploaded').length

  return {
    items,
    add,
    remove,
    clear,
    retryUpload,
    send,
    retrySend,
    readyCount,
    // True while anything in the tray is still being prepared, so the send
    // button can wait rather than quietly dropping the in-flight images.
    isPreparing: items.some(i => i.status === 'compressing' || i.status === 'uploading'),
    isSending,
    maxImages: MAX_BATCH_IMAGES,
    imageMaxBytes: getImageMaxBytes(channel),
  }
}
